// Persons in a logement, and what each one eats (PMS-GROUP-EDIT-BRIEF §3).
//
// The CLIENT MIRROR of three SQL functions, for the form's preview — the
// database stays the enforcer (ARCHITECTURE.md §1), and the two are kept on the
// same worked examples (apps/pms/src/stayPersons.test.ts and the migration
// probes of 20261018100100 / 20261018110100):
//
//   pmsMealTaken          pms_stay_person_meals   (the default rule)
//   pmsMealProductCode    pms_meal_product_code   (which product a meal bills)
//   pmsAnonymousPersons   pms_stay_person_fill    (heads → persons)
//
// Dates: a person is present on the NIGHTS [startDate, endDate). A dinner is
// eaten on the night; a breakfast and a packed lunch the morning after it.

import { pmsAddDays, pmsNightCents, pmsNightCount, pmsProductDates, type PmsBoard } from './pms.js';
import type {
  AgeBand,
  Allergen,
  Cents,
  IsoDate,
  MealPrestation,
  NightPrice,
  Product,
  ProductRequest,
  StayPersonMeal,
} from './types.js';

export const PMS_AGE_BANDS: readonly AgeBand[] = [
  'adult',
  'child_13_17',
  'child_9_12',
  'child_6_8',
  'child_3_5',
  'child_unknown',
  'infant',
];

export const PMS_AGE_BAND_LABELS_FR: Record<AgeBand, string> = {
  adult: 'Adulte',
  child_13_17: '13-17 ans',
  child_9_12: '9-12 ans',
  child_6_8: '6-8 ans',
  child_3_5: '3-5 ans',
  child_unknown: 'Enfant (âge ?)',
  infant: 'Bébé (0-2)',
};

export const PMS_MEALS: readonly MealPrestation[] = ['breakfast', 'packed_lunch', 'dinner'];

/** The 14 regulated allergens, in the order of Annex II of Règlement (UE)
 *  1169/2011 — the client mirror of `pms_allergen_codes()` (group edit step 5).
 *  Their French labels are LEGAL_ALLERGENS (@lemontaigu/shared), same order. */
export const PMS_ALLERGENS: readonly Allergen[] = [
  'gluten',
  'crustaceans',
  'eggs',
  'fish',
  'peanuts',
  'soybeans',
  'milk',
  'nuts',
  'celery',
  'mustard',
  'sesame',
  'sulphites',
  'lupin',
  'molluscs',
];

export const PMS_MEAL_LABELS_FR: Record<MealPrestation, string> = {
  breakfast: 'PDJ',
  packed_lunch: 'Panier',
  dinner: 'Dîner',
};

/**
 * The type de séjour's ONE label set for the whole PMS (group edit Q10): the
 * form, the Planning and Miroir Vega read this, and nothing spells a board out
 * on its own. The colour is the screen's (a CSS token keyed by the code).
 */
export const PMS_BOARD_LABELS: Record<PmsBoard, string> = {
  hb: 'DP',
  bb: 'Pdj',
  none: 'Ch',
};

export const PMS_BOARD_TITLES_FR: Record<PmsBoard, string> = {
  hb: 'Demi-pension',
  bb: 'Chambre et petit-déjeuner',
  none: 'Chambre seule',
};

/** Under 18 and over 2: a child for the levy and for capacity. */
export const pmsIsChild = (b: AgeBand): boolean => b !== 'adult' && b !== 'infant';

/** Does the type de séjour include this meal? Breakfast on Pdj and DP, dinner
 *  on DP. A packed lunch is never included (board brief Q5). */
export function pmsMealIncluded(board: PmsBoard, meal: MealPrestation): boolean {
  if (meal === 'breakfast') return board === 'bb' || board === 'hb';
  if (meal === 'dinner') return board === 'hb';
  return false;
}

/** The day a meal of NIGHT `night` is eaten. */
export const pmsMealServiceDate = (night: IsoDate, meal: MealPrestation): IsoDate =>
  meal === 'dinner' ? night : pmsAddDays(night, 1);

/** The nights a person is present: [startDate, endDate). */
export function pmsPersonNights(p: { startDate: IsoDate; endDate: IsoDate }): IsoDate[] {
  const out: IsoDate[] = [];
  for (let i = 0; i < pmsNightCount(p.startDate, p.endDate); i++) out.push(pmsAddDays(p.startDate, i));
  return out;
}

/** The default a person's exception overrides: included, or switched on for
 *  the room. */
export const pmsMealDefault = (
  board: PmsBoard,
  roomMeals: readonly MealPrestation[],
  meal: MealPrestation,
): boolean => pmsMealIncluded(board, meal) || roomMeals.includes(meal);

/** Does this person take this meal on this date? Their exception, else the
 *  default. A room switch NEVER overrides an exception (Q6). */
export function pmsMealTaken(
  board: PmsBoard,
  roomMeals: readonly MealPrestation[],
  exceptions: readonly Pick<StayPersonMeal, 'serviceDate' | 'prestation' | 'taken'>[],
  serviceDate: IsoDate,
  meal: MealPrestation,
): boolean {
  const x = exceptions.find((e) => e.serviceDate === serviceDate && e.prestation === meal);
  return x === undefined ? pmsMealDefault(board, roomMeals, meal) : x.taken;
}

/** The catalogue code a meal TAKEN AND NOT INCLUDED bills. null = nothing:
 *  a dinner (included on DP, a planned cover at the carte elsewhere), an
 *  infant (D1), or a child whose band is not yet known. 13–17 bills the adult
 *  PDJ (operator, 2026-10-18). */
export function pmsMealProductCode(meal: MealPrestation, band: AgeBand): string | null {
  if (band === 'infant') return null;
  if (meal === 'packed_lunch') return 'PANIER';
  if (meal !== 'breakfast') return null;
  switch (band) {
    case 'adult':
    case 'child_13_17':
      return 'PDJ';
    case 'child_9_12':
      return 'PDJ_ENF_9_12';
    case 'child_6_8':
      return 'PDJ_ENF_6_8';
    case 'child_3_5':
      return 'PDJ_ENF_0_5';
    default:
      return null;
  }
}

/** The logement's counts, derived from its persons (Q2). */
export function pmsCountsFromPersons(persons: readonly { ageBand: AgeBand }[]): {
  adults: number;
  children: number;
  infants: number;
} {
  let adults = 0;
  let children = 0;
  let infants = 0;
  for (const p of persons) {
    if (p.ageBand === 'adult') adults++;
    else if (p.ageBand === 'infant') infants++;
    else children++;
  }
  return { adults, children, infants };
}

/** « 2A 1E 1B » — the rooming list's Pers. column. */
export function pmsPersonsShort(persons: readonly { ageBand: AgeBand }[]): string {
  const c = pmsCountsFromPersons(persons);
  return [c.adults > 0 ? `${c.adults}A` : '', c.children > 0 ? `${c.children}E` : '', c.infants > 0 ? `${c.infants}B` : '']
    .filter((s) => s !== '')
    .join(' ');
}

/** Anonymous persons for counted heads, present for the whole stay — what the
 *  database creates for a booking of « 2 adultes » (pms_stay_person_fill). */
export function pmsAnonymousPersons(
  adults: number,
  children: number,
  infants: number,
): { ageBand: AgeBand }[] {
  return [
    ...Array.from({ length: Math.max(adults, 0) }, () => ({ ageBand: 'adult' as AgeBand })),
    ...Array.from({ length: Math.max(children, 0) }, () => ({ ageBand: 'child_unknown' as AgeBand })),
    ...Array.from({ length: Math.max(infants, 0) }, () => ({ ageBand: 'infant' as AgeBand })),
  ];
}

// ── The preview of one logement ─────────────────────────────────────────────

export interface PreviewPerson {
  ageBand: AgeBand;
  startDate: IsoDate;
  endDate: IsoDate;
  exceptions: readonly Pick<StayPersonMeal, 'serviceDate' | 'prestation' | 'taken'>[];
}

/** One meal column of the rooming list, for one logement. */
export interface MealCell {
  /** Meals taken over the stay (person × day), infants excluded as on the board. */
  taken: number;
  /** The slots that could have been taken (person-nights, infants excluded). */
  slots: number;
  /** The type de séjour includes it: an effectif, no charge. */
  included: boolean;
  /** Taken, not included, and billed (a product exists for the band). */
  billed: number;
  /** Taken, not included, and billing NOTHING because a child's band is
   *  unknown — the desk should set it. */
  unbilledUnknownBand: number;
}

export interface RoomPreview {
  roomCents: Cents;
  /** Agreed extras (requested or auto-applied), per-person ones by presence. */
  productCents: Cents;
  /** Meals billed per person (breakfast by band, packed lunch). */
  mealCents: Cents;
  taxCents: Cents;
  totalCents: Cents;
  /** Adult person-nights — what the commune is declared. */
  taxableNights: number;
  /** Minor and infant person-nights — exempt. */
  exemptNights: number;
  meals: Record<MealPrestation, MealCell>;
}

/**
 * What one logement will cost and eat, from its persons — the mirror of
 * pms_write_charges + pms_derive_occupancy_charges for the form.
 *
 * `catalogue` is the établissement's. Meal products are looked up by code; a
 * code the catalogue lacks bills nothing here, while the database refuses the
 * save with a sentence naming it (better told than silently free).
 */
export function pmsPreviewRoom(input: {
  board: PmsBoard;
  roomMeals: readonly MealPrestation[];
  nights: readonly NightPrice[];
  checkin: IsoDate;
  checkout: IsoDate;
  persons: readonly PreviewPerson[];
  catalogue: readonly Product[];
  requested: readonly ProductRequest[];
}): RoomPreview {
  const { board, roomMeals, persons, catalogue } = input;
  const roomCents = input.nights.reduce((s, n) => s + pmsNightCents(n), 0);
  const stayNights = pmsPersonNights({ startDate: input.checkin, endDate: input.checkout });

  // Presence per night: adults + children (infants never multiply, D1).
  const presentOn = (night: IsoDate) =>
    persons.filter((p) => p.ageBand !== 'infant' && p.startDate <= night && p.endDate > night).length;

  let productCents = 0;
  for (const p of catalogue) {
    if (!p.active || p.isTax || p.boardPrestation) continue;
    const asked = input.requested.find((r) => r.productId === p.id);
    if (asked === undefined && !p.autoApply) continue;
    if (p.perPerson && p.autoApply) {
      // Derived per span, i.e. per night of presence (pms_derive_occupancy_charges).
      for (const night of stayNights) productCents += Math.max(presentOn(night), 1) * p.priceCents;
    } else {
      const units = Math.max(asked?.quantity ?? 1, 1);
      productCents += units * p.priceCents * pmsProductDates(p.appliesTo, input.checkin, input.checkout).length;
    }
  }

  let taxableNights = 0;
  let exemptNights = 0;
  for (const p of persons) {
    const n = pmsNightCount(p.startDate, p.endDate);
    if (p.ageBand === 'adult') taxableNights += n;
    else exemptNights += n;
  }
  let taxCents = 0;
  for (const t of catalogue) if (t.active && t.isTax) taxCents += taxableNights * t.priceCents;

  const meals = {} as Record<MealPrestation, MealCell>;
  let mealCents = 0;
  for (const meal of PMS_MEALS) {
    const cell: MealCell = { taken: 0, slots: 0, included: pmsMealIncluded(board, meal), billed: 0, unbilledUnknownBand: 0 };
    for (const p of persons) {
      if (p.ageBand === 'infant') continue;
      for (const night of pmsPersonNights(p)) {
        cell.slots++;
        const date = pmsMealServiceDate(night, meal);
        if (!pmsMealTaken(board, roomMeals, p.exceptions, date, meal)) continue;
        cell.taken++;
        if (cell.included) continue;
        const code = pmsMealProductCode(meal, p.ageBand);
        if (code === null) {
          if (p.ageBand === 'child_unknown' && meal === 'breakfast') cell.unbilledUnknownBand++;
          continue;
        }
        const product = catalogue.find((x) => x.code === code && x.active);
        if (product === undefined) continue;
        cell.billed++;
        mealCents += product.priceCents;
      }
    }
    meals[meal] = cell;
  }

  return {
    roomCents,
    productCents,
    mealCents,
    taxCents,
    totalCents: roomCents + productCents + mealCents + taxCents,
    taxableNights,
    exemptNights,
    meals,
  };
}

